<script setup>
import { computed } from 'vue'
import { Badge } from '@drama/ui'

import { useAuditText } from './audit-text.js'
import { asArray } from './run-observer.js'

const props = defineProps({
  // 一次回合请求的校验反馈：快照请求的 diagnostics，与该请求 context_record 的 diagnostics 是同一份数组。
  diagnostics: { type: Array, required: true },
})

// 模型原文所在的字段：内容长，折叠显示。
const RAW_FIELDS = ['raw', 'record']

const { tr, enumLabel } = useAuditText()

/**
 * 每条反馈展开成"种类 + 一行文字 + 可折叠原文"：文字由 error、text、issues 与其余字段拼成，
 * 模型原文（raw、record）和记录失败时的分段正文（segments）进原文。
 */
const entries = computed(() => props.diagnostics.map((item, index) => {
  const fields = item !== null && typeof item === 'object' ? item : { text: String(item) }
  const parts = []
  const raw = []
  for (const [key, value] of Object.entries(fields)) {
    if (key === 'kind' || value === null || value === undefined || value === '') continue
    if (RAW_FIELDS.includes(key)) raw.push(typeof value === 'string' ? value : JSON.stringify(value, null, 2))
    else if (key === 'segments') raw.push(asArray(value).map((segment) => segment?.text ?? '').join('\n'))
    else if (key === 'issues') parts.push(...asArray(value).map(issueText))
    else if (key === 'error' || key === 'text') parts.push(String(value))
    else if (typeof value !== 'object') parts.push(`${key}: ${value}`)
    else parts.push(`${key}: ${JSON.stringify(value)}`)
  }
  return { key: index, kind: String(fields.kind ?? 'unknown'), text: parts.join(' · '), raw: raw.filter(Boolean).join('\n\n') }
}))

function issueText(issue) {
  if (typeof issue === 'string') return issue
  return issue?.message ?? issue?.text ?? issue?.reason ?? JSON.stringify(issue)
}
</script>

<template>
  <ul class="au-lines" data-testid="observer-request-diagnostics">
    <li v-for="entry in entries" :key="entry.key" class="au-diagnostic">
      <span class="au-chips">
        <Badge size="sm" variant="warning" :title="entry.kind">{{ enumLabel('diagnostics.kinds', entry.kind) }}</Badge>
        <span>{{ entry.text || '—' }}</span>
      </span>
      <details v-if="entry.raw" class="au-message">
        <summary>{{ tr('diagnostics.raw', { count: entry.raw.length }) }}</summary>
        <pre class="au-pre">{{ entry.raw }}</pre>
      </details>
    </li>
  </ul>
</template>
